<template>
  <div class="trip-list">
    <div class="trip-card-compact" v-for="trip in trips" :key="trip.recordLocator">
      <div class="trip-desktop-layout" :class="{ 'force-stacked': forceStacked }">
        <div class="trip-main-content">
          <div class="trip-content-wrapper">
            <div class="trip-info-compact">
              <div class="trip-title-row">
                <h6 class="trip-title-compact">{{ getTripName(trip) }}</h6>
                <b-tag
                  v-if="trip.tripSummary.daysUntilStart <= 30 && trip.tripSummary.daysUntilStart > 0"
                  type="is-secondary"
                  size="is-small"
                  class="trip-countdown-tag"
                >
                  {{ getDaysUntilTripText(trip.tripSummary.daysUntilStart) }}
                </b-tag>
              </div>
              <div class="trip-details-compact">
                <div class="trip-date-compact">
                  {{ formatTripDatesCompact(trip.startDate, trip.endDate) }}
                </div>
                <div class="trip-locator-compact">
                  Reference number: <span class="record-locator-code">{{ trip.recordLocator }}</span>
                </div>
              </div>
              <div class="trip-segments-compact mt-4" v-if="trip.segments && trip.segments.length > 0">
                <span
                  v-for="segmentType in getUniqueSegmentTypes(trip.segments)"
                  :key="segmentType"
                  class="segment-badge-compact"
                  :class="`segment-${segmentType}`"
                >
                  <SegmentIcon :type="segmentType" size="is-small" />
                  {{ segmentType.charAt(0).toUpperCase() + segmentType.slice(1) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="trip-action-column">
          <b-button
            type="is-primary"
            size="is-small"
            @click="viewTripDetail(trip.recordLocator)"
            class="trip-view-button-compact"
          >
            View Trip Details
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SegmentIcon from '../UI/SegmentIcon.vue'

export default {
  name: 'TripListCompact',
  components: {
    SegmentIcon
  },
  props: {
    trips: {
      type: Array,
      required: true,
      default: () => []
    },
    forceStacked: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    getTripName(trip) {
      if (trip.tripSummary.tripName) {
        return trip.tripSummary.tripName
      }
      return trip.tripSummary.cityState
    },

    formatTripDatesCompact(startDate, endDate) {
      // Check if dates are valid - handle various falsy values
      if (!startDate || startDate === 'null' || startDate === '' ||
          !endDate || endDate === 'null' || endDate === '') {
        return 'Dates not available'
      }

      const startMoment = this.$moment(startDate)
      const endMoment = this.$moment(endDate)

      // Check if moment objects are valid
      if (!startMoment.isValid() || !endMoment.isValid()) {
        return 'Dates not available'
      }

      const start = startMoment.format('ddd, MMM D')
      const end = endMoment.format('ddd, MMM D')

      // Compare dates properly
      if (startMoment.isSame(endMoment, 'day')) {
        return startMoment.format('ddd, MMM D')
      }
      return `${start} - ${end}`
    },

    getUniqueSegmentTypes(segments) {
      if (!segments || !Array.isArray(segments)) return []

      const types = segments
        .filter(segment => segment.status !== 'cancelled')
        .map(segment => segment.type)

      return [...new Set(types)]
    },

    getDaysUntilTripText(days) {
      if (days === 1) {
        return 'Tomorrow'
      } else if (days <= 7) {
        return `${days} days`
      } else {
        return `${days} days`
      }
    },

    viewTripDetail(recordLocator) {
      const encodedLocator = this.$helpers.encryptText(recordLocator)
      this.$router.push(`/trip-detail/${encodedLocator}`)
    }
  }
}
</script>

<style scoped>
/* Trip List Container */
.trip-list {
  container-type: inline-size;
}

/* Trip Card Styles - Mobile-First Compact */
.trip-card-compact {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.trip-card-compact:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.trip-card-compact:last-child {
  margin-bottom: 0;
}

/* Desktop layout - two columns */
.trip-desktop-layout {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.trip-main-content {
  flex: 1;
  min-width: 0;
}

.trip-action-column {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* Auto-stack when container is narrow (like Home V2 sidebar) */
@container (max-width: 500px) {
  .trip-desktop-layout {
    flex-direction: column !important;
    gap: 1rem;
  }

  .trip-action-column {
    align-self: stretch;
  }

  .trip-view-button-compact {
    width: 100% !important;
  }
}

/* Fallback for browsers without container query support */
/* Stack when the trip card itself gets too narrow */
@media (max-width: 600px) {
  .trip-desktop-layout {
    flex-direction: column !important;
    gap: 1rem;
  }

  .trip-action-column {
    align-self: stretch;
  }

  .trip-view-button-compact {
    width: 100% !important;
  }
}

.trip-content-wrapper {
  display: flex;
  gap: 1rem;
}

.trip-info-compact {
  flex: 1;
  min-width: 0;
}

.trip-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
}

.trip-title-compact {
  font-size: 1rem;
  font-weight: 600;
  color: #363636;
  margin: 0;
  line-height: 1.2;
  flex: 1;
}

.trip-countdown-tag {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 500;
}

.trip-details-compact {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.trip-date-compact,
.trip-locator-compact {
  margin-bottom: 0.25rem;
}

.record-locator-code {
  font-weight: 600;
  color: #a4393a;
  background: rgba(164, 57, 58, 0.1);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.trip-segments-compact {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.segment-badge-compact {
  background: #eeeeee;
  color: #434343;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 500;
}

.segment-badge-compact .icon {
  font-size: 0.75rem;
}

.trip-view-button-compact {
  white-space: nowrap;
}

/* Force stacked layout (for narrow containers like Home V2) */
.trip-desktop-layout.force-stacked {
  flex-direction: column !important;
  gap: 1rem;
}

.trip-desktop-layout.force-stacked .trip-action-column {
  align-self: stretch;
}

.trip-desktop-layout.force-stacked .trip-view-button-compact {
  width: 100% !important;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .trip-card-compact {
    padding: 0.75rem;
  }

  /* Mobile: stack layout vertically */
  .trip-desktop-layout {
    flex-direction: column;
    gap: 1rem;
  }

  .trip-action-column {
    min-width: auto;
  }

  .trip-view-button-compact {
    width: 100%;
  }

  .trip-title-row {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .trip-segments-compact {
    flex-wrap: wrap;
  }
}
</style>
